// TODO: Refactor code so that disables are not required
/* eslint-disable sonarjs/cognitive-complexity,max-nested-callbacks */
import { get } from 'lodash';
import { useEffect, useState } from 'react';

import { CategoryRef } from '~/mappers/itemListing/models';
import { gtmService } from '~/services/gtm';
import { SalsifyProductData } from '~/types/common';
import {
  generateLocationQueryString,
  getLocationQueryStringParam,
} from '~/utils/browser';
import { QUERY_STRING_PARAM_STRATEGY } from '~/utils/constants';

import { QUERY_STRING_PARAMS } from './constants';
import { SORT_BY, SortBy, UseFilterOptions } from './models';
import { getFilterValuesFromQueryString } from './utils';

export const useItemFilters = <T = unknown>(
  items: T[],
  { fields, filters }: UseFilterOptions
) => {
  if (!fields.name || !fields.createdAt || !filters.length) {
    throw new Error('Missing configuration for listing filters');
  }

  const [data, setData] = useState(items);
  const [filter, setFilter] = useState<{ group: string; value: CategoryRef }[]>(
    getFilterValuesFromQueryString(
      filters,
      getLocationQueryStringParam(QUERY_STRING_PARAMS.FILTER) as string[] | []
    )
  );
  const [sort, setSort] = useState<SortBy>(
    (getLocationQueryStringParam(
      QUERY_STRING_PARAMS.SORT,
      QUERY_STRING_PARAM_STRATEGY.STRING
    ) as SortBy) || SORT_BY.DEFAULT
  );
  const [activeGroup, setActiveGroup] = useState('');

  const handleGroupReset = (name: string) =>
    setFilter((prev) => prev.filter((item) => item.group !== name));

  const getActiveCountForGroup = (name: string) =>
    filter.filter((item) => item.group === name).length;

  const handleActiveGroup = (group: string) => {
    const isGroupActive = group === activeGroup;

    setActiveGroup(isGroupActive ? '' : group);
  };

  const handleFilterChange = (group: string, value: CategoryRef) => {
    const { uid } = value;

    setActiveGroup(group);

    if (!filter.find((item) => item.value.uid === uid)) {
      setFilter((prev) => [...prev, { group, value }]);
    } else {
      setFilter((prev) => prev.filter((item) => item.value.uid !== uid));
    }
  };

  const handleSortChange = (sortBy: SortBy) => {
    setSort(sortBy);
  };

  useEffect(() => {
    let sorted = [...items];

    if (sort !== SORT_BY.DEFAULT) {
      sorted = sorted.sort((a, b) => {
        switch (sort) {
          case SORT_BY.NAME_ASC: {
            if (get(a, fields.name) < get(b, fields.name)) {
              return -1;
            }

            if (get(a, fields.name) > get(b, fields.name)) {
              return 1;
            }

            return 0;
          }

          case SORT_BY.NAME_DESC: {
            if (get(a, fields.name) < get(b, fields.name)) {
              return 1;
            }

            if (get(a, fields.name) > get(b, fields.name)) {
              return -1;
            }

            return 0;
          }

          case SORT_BY.RECENT:
            return (
              new Date(get(b, fields.createdAt)).valueOf() -
              new Date(get(a, fields.createdAt)).valueOf()
            );

          default:
            return 0;
        }
      });
    }

    const groups = [...new Set(filter.map((item) => item.group))]
      .map((name) => ({
        name,
        uids: filter
          .filter(({ group }) => group === name)
          .map((item) => item.value.uid),
      }))
      .filter(({ uids }) => uids.length > 0);

    const filtredItems = sorted.filter((item) => {
      const itemHaveCategories =
        typeof item === 'object' && item !== null && 'categories' in item;

      if (filter.length === 0 || !itemHaveCategories) return true;

      const categories = item.categories as CategoryRef[];

      return groups.every((group) =>
        categories.some((category) => group.uids.includes(category.uid))
      );
    });

    const filteredItemsProductData = (
      filtredItems as {
        fields?: { productData: unknown };
        productData?: unknown;
      }[]
    )?.map((props) =>
      props.fields ? props.fields.productData : props.productData
    );

    gtmService.emitViewItemList(
      filteredItemsProductData as SalsifyProductData[],
      {
        ...(filter.length && { item_list_filter: activeGroup }),
        item_list_sort: sort,
      }
    );

    setData(groups.length > 0 ? filtredItems : sorted);

    const getActiveFilterUids = filter.map(
      ({ value: { uid: filterUid } }) => filterUid
    );

    window.history.pushState(
      null,
      '',
      generateLocationQueryString({
        sort,
        filter: getActiveFilterUids,
      })
    );
  }, [fields.createdAt, fields.name, filter, items, sort]);

  return {
    data,
    filter,
    sort,
    handleFilterChange,
    handleSortChange,
    handleGroupReset,
    handleActiveGroup,
    getActiveCountForGroup,
    activeGroup,
  };
};
